import * as colors from '@giphy/colors'
import { setGADataLayer, setUser } from 'analytics'
import qs from 'query-string'
import { useContext, useRef, useState } from 'react'
import { useLocalStorage } from 'react-use'
import { login } from 'shared/api/auth'
import AppleButton from 'shared/components/form/apple-button'
import { ForgotPassword, TermsOfServiceLogin } from 'shared/components/form/copy'
import useCSRFToken from 'shared/components/form/csrftoken-hook'
import FacebookButton from 'shared/components/form/facebook-button'
import LoginFields, { FIELDS } from 'shared/components/form/fields'
import { CTAButton as BrandCTAButton, Content, FormContainer } from 'shared/components/form/form-components'
import appendNextUrl from 'shared/components/form/get-next-url'
import MessageContext from 'shared/contexts/message'
import styled from 'styled-components'

const sectionMargin = 6

const FacebookButtonWithMargin = styled(FacebookButton)`
    margin-bottom: ${sectionMargin}px;
`

const AppleButtonWithMargin = styled(AppleButton)`
    margin-bottom: ${sectionMargin}px;
`

const FormContent = styled(Content)<{ width: number }>`
    width: ${(props) => props.width}px;
`

const CTAButton = styled(BrandCTAButton)`
    width: 100%;
    background: linear-gradient(-135deg, #6157ff 0%, #9933ff 100%);
    opacity: ${(props) => (props.disabled ? '60' : '100')}% !important;
    font-size: 15px;
    border-radius: 3px;
    cursor: pointer;
`

type LoginFormProps = {
    width: number
}

const LoginForm = ({ width }: LoginFormProps) => {
    const [submitEnabled, setSubmitEnabled] = useState<Boolean>(false)
    const { CSRFToken } = useCSRFToken()
    const { sendMessage } = useContext(MessageContext)
    const formRef = useRef<HTMLFormElement | null>(null)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_, setLoginMethod] = useLocalStorage('login_type')

    const onValidChange = (isFormValid: Boolean) => {
        setSubmitEnabled(isFormValid)
    }

    const submit = async (event) => {
        event.stopPropagation()
        event.preventDefault()

        if (submitEnabled && formRef.current) {
            const { next } = qs.parse(location.search) || ''
            try {
                const response = await login({
                    email: formRef.current['email'].value,
                    password: formRef.current['password'].value,
                    next,
                })

                setLoginMethod('email')
                setUser({
                    loggedInStatus: true,
                    userType: 'user',
                    verifiedStatus: response.is_verified,
                    userID: response.id,
                    loginMethod: 'email',
                })
                setGADataLayer({
                    event: 'login_form_success',
                    options: {
                        form: {
                            formName: 'login',
                        },
                    },
                })

                // If there is no catch, we can just reload the page and the backend will
                // redirect to the channels page with the user logged
                location.reload()
            } catch (err) {
                setGADataLayer({
                    event: 'login_form_error',
                    options: {
                        form: {
                            formName: 'login',
                            formError: 'email,password',
                        },
                    },
                })
                sendMessage(err.details.detail, 'error')
            }
        }
    }

    return (
        <FormContainer>
            <form method="POST" action={appendNextUrl(`/login`)} onSubmit={submit} ref={formRef}>
                <FormContent width={width}>
                    {CSRFToken && <input type="hidden" name="csrfmiddlewaretoken" value={CSRFToken} />}
                    <LoginFields
                        onValidChange={onValidChange}
                        showFields={[FIELDS.email, FIELDS.password]}
                        borderRadius={3}
                    />
                    <CTAButton
                        color={submitEnabled ? colors.primaryCTA : colors.giphyDarkGrey}
                        disabled={!submitEnabled}
                    >
                        Log in
                    </CTAButton>
                    <ForgotPassword />
                    <FacebookButtonWithMargin
                        label="Log In with Facebook"
                        onClick={() => (location.href = `/login/facebook/${location.search}`)}
                    />
                    <AppleButtonWithMargin
                        label="Log In with Apple"
                        onClick={() => (location.href = `/login/apple/${location.search}`)}
                    />
                    <TermsOfServiceLogin />
                </FormContent>
            </form>
        </FormContainer>
    )
}

export default LoginForm
